import { useState, useEffect, Dispatch, SetStateAction } from 'react';

/**
 * 尝试修复双重JSON编码的值（历史遗留问题）
 * 某些早期版本可能意外将值编码了两次
 */
const fixDoubleEncodedValue = (parsed: any, key: string): any => {
  if (typeof parsed === 'string' && parsed.startsWith('"') && parsed.endsWith('"')) {
    try {
      const fixed = JSON.parse(parsed);
      // 自动修复localStorage中的数据
      window.localStorage.setItem(key, JSON.stringify(fixed));
      console.log(`Fixed double-encoded value for key "${key}"`);
      return fixed;
    } catch (e) {
      // 如果不是有效的JSON，返回原始解析值
      return parsed;
    }
  }
  return parsed;
};

/**
 * 本地存储Hook
 * 提供持久化状态管理，自动同步跨标签页
 */
export const useLocalStorage = <T>(
  key: string,
  initialValue: T
): [T, Dispatch<SetStateAction<T>>] => {
  // 从localStorage获取初始值
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (!item) return initialValue;

      const parsed = JSON.parse(item);
      // 修复潜在的双重编码问题
      return fixDoubleEncodedValue(parsed, key) as T;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // 设置值并同步到localStorage
  const setValue: Dispatch<SetStateAction<T>> = (value) => {
    try {
      // 支持函数式更新
      const valueToStore = value instanceof Function ? value(storedValue) : value;

      setStoredValue(valueToStore);

      if (valueToStore === undefined) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  // 监听其他标签页的localStorage变化
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          const parsed = JSON.parse(e.newValue);
          // 修复潜在的双重编码问题
          const fixed = fixDoubleEncodedValue(parsed, key) as T;
          setStoredValue(fixed);
        } catch (error) {
          console.error(`Error parsing localStorage key "${key}":`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return [storedValue, setValue];
};
